import React, { useEffect, useRef, useState } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import BarChartIcon from '@mui/icons-material/BarChart';
import AddIcon from '@mui/icons-material/Add';
import {
    gridFilteredSortedRowIdsSelector,
    gridSortedRowIdsSelector,
    useGridApiContext,
} from '@mui/x-data-grid';
import {
    Box, Checkbox, createSvgIcon, IconButton, Popover, Table,
    TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography,
} from '@mui/material';
import { Edit, Close } from '@mui/icons-material';
import FileCopyIcon from '@mui/icons-material/FileCopy';

import { IndigoSvg } from 'BMapsSrc/IndigoSvg';
import BMapsTooltip from '../../BMapsTooltip';
import { getNumberedCompoundName } from '../../dialog/ScreeningPane/Tabs/ScreeningResults';
import { EditInPlaceInput, HoverableCompoundStructureCell } from '../TableCells';
import { TextArea } from '../../common/TextFields';
import CopyToClipboardButton from '../../common/Buttons/CopyToClipboardButton';
import { useToolBarId } from './ToolBarIdContext';

/**
 * @typedef {import(
 *   '../../dialog/ScreeningPane/Tabs/ScreeningResults.jsx'
 * ).ScreeningCompoundInfo} ScreeningCompoundInfo
 */

/**
 * Export-menu button - shared by every toolbar in this directory (GridToolbar,
 * CoralToolbar). Takes a plain, fully-built exportOptions array; never constructs one
 * itself - that's always the responsibility of the top-level caller that actually has
 * the data/context a given option needs (see ScreeningResults.jsx's
 * makeStructuresZipOption and this file's own CsvExportButton for the two examples of
 * where that lives). Self-hides on an empty/missing list, so callers can render it
 * unconditionally as a plain child rather than each writing their own
 * `!!exportOptions.length &&` guard.
 */
export function ExportButton({ exportOptions }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const toolBarId = useToolBarId();

    if (!exportOptions || exportOptions.length === 0) return null;

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const open = Boolean(anchorEl);

    const ExportIcon = createSvgIcon(
        <path d="M19 12v7H5v-7H3v7c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zm-6 .67l2.59-2.58L17 11.5l-5 5-5-5 1.41-1.41L11 12.67V3h2z" />,
        'SaveAlt',
    );

    return (
        <>
            <Button
                color="primary"
                size="small"
                startIcon={<ExportIcon />}
                aria-describedby={`${toolBarId}-export`}
                onClick={handleClick}
            >
                Export
            </Button>
            <Menu
                id={`${toolBarId}-export`}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
            >
                {exportOptions.map((option) => (
                    <MenuItem
                        key={option.title}
                        disabled={!!option.disabled}
                        onClick={() => {
                            handleClose();
                            option.handleExport();
                        }}
                    >
                        {option.title}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

/**
 * The two CSV export entries any DataGrid-backed table gets, plus whatever
 * table-specific entries the caller supplies (e.g. screening's "Download 3D data
 * (.zip)", via `extraOptions`). Needs useGridApiContext() - only works rendered inside a
 * DataGrid's own toolbar slot tree - which is why this owns building these two entries
 * itself rather than leaving it to the caller: nothing else in that render tree has
 * that access. Callers compose this as just another child alongside ChartButton, a
 * leading button, etc. - GridToolbar builds nothing except its own baked-in
 * column/density buttons (see CustomToolbars.jsx). No toolBarId prop - like every
 * button here, it reads that from ToolBarIdContext instead.
 * @param {{
 *   fileName?: string,
 *   extraOptions?: ({title: string, disabled?: boolean, handleExport: () => void} | null)[],
 * }} param0
 */
export function CsvExportButton({ fileName, extraOptions }) {
    const ref = useGridApiContext();
    const name = fileName || `BMaps Table ${new Date().toLocaleDateString('en-US')}`;
    const exportOptions = [
        {
            title: 'Export All to CSV',
            handleExport: () => ref.current.exportDataAsCsv({
                allColumns: true,
                getRowsToExport: ({ apiRef }) => gridSortedRowIdsSelector(apiRef),
                fileName: name,
                includeColumnGroupsHeaders: false,
            }),
        },
        {
            title: 'Export Visible to CSV',
            handleExport: () => ref.current.exportDataAsCsv({
                getRowsToExport: ({ apiRef }) => gridFilteredSortedRowIdsSelector(apiRef),
                fileName: name,
                includeColumnGroupsHeaders: false,
            }),
        },
        ...(extraOptions || []),
    ].filter(Boolean);
    return <ExportButton exportOptions={exportOptions} />;
}

/**
 * Chart-picker button - shared by every toolbar in this directory. Self-hides when
 * there are 0 or 1 charts to offer - nothing to switch to with only one, and callers
 * render this unconditionally as a plain child rather than each computing that
 * threshold themselves (this used to be two different thresholds depending on which
 * toolbar wrapper you went through - >1 for GridToolBar, any-at-all for the Coral
 * toolbars - which was never a deliberate difference, just an accident of who wrote
 * which caller-side guard; unifying it here removes the inconsistency, not just the
 * duplication).
 */
export function ChartButton({ charts, setActiveChart }) {
    const [chartAnchorEl, setChartAnchorEl] = useState(null);
    const toolBarId = useToolBarId();

    if (!charts || charts.length <= 1) return null;

    const handleCharts = (event) => {
        setChartAnchorEl(event.currentTarget);
    };

    const handleChartClose = () => {
        setChartAnchorEl(null);
    };

    const openChart = Boolean(chartAnchorEl);
    return (
        <>
            <Button
                color="primary"
                size="small"
                startIcon={<BarChartIcon />}
                aria-describedby={`${toolBarId}-charts`}
                onClick={handleCharts}
            >
                Charts
            </Button>
            <Menu
                id={`${toolBarId}-charts`}
                anchorEl={chartAnchorEl}
                open={openChart}
                onClose={handleChartClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
            >
                {charts.map((chart) => (
                    <MenuItem
                        key={chart.id}
                        onClick={() => { setActiveChart(chart.id); }}
                    >
                        {chart.name}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

/**
 * "Compound" button - the Scoring Coral map's leading button, used as a plain child of
 * CoralToolbar by ScoringCoralMap. `compoundOptions` here is a list of pre-built actions
 * (`{title, onClick, smiles?}`), not raw data to edit - unlike EditCompoundsButton's
 * `compoundOptions`, which is the raw ScreeningCompoundInfo[] itself. Same prop name,
 * deliberately different shape - see the two buttons' own docs for why they aren't
 * meant to share an implementation.
 */
export function AddCompoundButton({ compoundOptions }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const toolBarId = useToolBarId();

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const open = Boolean(anchorEl);
    return (
        <>
            <Button
                color="primary"
                size="small"
                startIcon={<AddIcon />}
                aria-describedby={`${toolBarId}-compound`}
                onClick={handleClick}
            >
                Compound
            </Button>
            <Menu
                id={`${toolBarId}-compound`}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
            >
                {compoundOptions.map((option) => (
                    <MenuItem
                        key={option.title}
                        onClick={() => {
                            handleClose();
                            option.onClick();
                        }}
                    >
                        <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            height: '3rem',
                            justifyContent: 'space-between',
                            width: '100%',
                        }}
                        >
                            <span style={{ flex: 1 }}>{option.title}</span>

                            {!!option.smiles && (
                                <IndigoSvg
                                    smiles={option.smiles}
                                    style={{
                                        padding: '0',
                                        height: '100%',
                                        width: '100%',
                                    }}
                                    autoUpdate
                                    alt={`2D structure of ${option.title}`}
                                />
                            )}

                        </span>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

/**
 * "Compounds" button - opens a popover to edit/reorder/color a list of compound
 * records. Used as a plain child of both GridToolbar and CoralToolbar - two real
 * consumers in two different files, hence living here rather than next to either. Both
 * consumers happen to be screening today (compoundOptions is a ScreeningCompoundInfo[]
 * - see ScreeningCompoundTable below, which does the actual editing), but that's a fact
 * about who calls this, not something the button itself needs to keep restating. Edits
 * accumulate locally and only reach the caller on close (`updateCompoundInfo`), not per
 * keystroke/toggle - see handleClose. Self-hides when there's no list to edit, same
 * reasoning as ExportButton/ChartButton's self-guards above.
 */
export function EditCompoundsButton({
    compoundOptions, updateCompoundInfo, useColorOptions,
}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [localCompoundInfoList, setLocalCompoundInfoList] = useState(compoundOptions);
    const toolBarId = useToolBarId();

    if (!compoundOptions) return null;

    const handleClose = () => {
        updateCompoundInfo(localCompoundInfoList);
        setAnchorEl(null);
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const open = Boolean(anchorEl);
    return (
        <>
            <Button
                color="primary"
                size="small"
                startIcon={<Edit />}
                aria-describedby={`${toolBarId}-compound`}
                onClick={handleClick}
            >
                Compounds
            </Button>
            <Popover
                id={`${toolBarId}-compound`}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography
                        variant="h6"
                        sx={{
                            margin: '0.5rem 0.5rem 0 1rem', display: 'flex', flexDirection: 'row', justifyContent: 'space-between',
                        }}
                    >
                        Configure compounds
                        <IconButton onClick={handleClose}><Close /></IconButton>
                    </Typography>
                    <ScreeningCompoundTable
                        screeningCompoundInfoList={localCompoundInfoList}
                        updateScreeningCompoundInfo={setLocalCompoundInfoList}
                        useColorOptions={useColorOptions}
                    />
                </Box>
            </Popover>
        </>
    );
}

/**
 * The actual editable table inside EditCompoundsButton's popover - one row per
 * compound, editing `screeningCompoundInfoList` (the popover's local draft copy, not
 * the caller's list directly - see EditCompoundsButton's handleClose).
 * @param {{
 * screeningCompoundInfoList: ScreeningCompoundInfo[],
 * updateScreeningCompoundInfo: (list: ScreeningCompoundInfo[]) => void,
 * useColorOptions: boolean,
 * }} param0
 */
function ScreeningCompoundTable({
    screeningCompoundInfoList, updateScreeningCompoundInfo,
    useColorOptions,
}) {
    if (!screeningCompoundInfoList || screeningCompoundInfoList.length === 0) return null;
    return (
        <>
            <TableContainer sx={{ margin: '0 0 0.5rem 0', overflow: 'unset' }}>
                <Table aria-label="Coral Compounds" size="small">
                    <TableHead />
                    <TableBody>
                        {screeningCompoundInfoList.map((screeningCompoundInfo, i) => (
                            <React.Fragment key={screeningCompoundInfo.smiles}>
                                {!!screeningCompoundInfo && (
                                    <ScreeningCompoundRow
                                        compoundIndex={i}
                                        screeningCompoundInfoList={screeningCompoundInfoList}
                                        updateScreeningCompoundInfo={
                                            updateScreeningCompoundInfo
                                        }
                                        useColorOptions={useColorOptions}
                                    />
                                )}
                            </React.Fragment>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
}

function ScreeningCompoundRow({
    compoundIndex, screeningCompoundInfoList, updateScreeningCompoundInfo, useColorOptions,
}) {
    const [editingName, setEditingName] = useState(false);
    const colorPickerRef = useRef(null);

    // onChange event in the color picker is fired while the color picker is open -
    // this can cause an update loop of whatever's consuming this compound's color being
    // set over and over again. In practice that's only ever the coral map's node colors
    // (useColorOptions, and so this color-picker cell at all, is only ever true via
    // CoralToolbar's ScreeningCoralMap - GridToolBar's table-view caller never passes
    // it). To avoid the loop, we listen to the change event and only update color when
    // the picker is closed.
    useEffect(() => {
        if (!colorPickerRef?.current) return () => {};
        const el = colorPickerRef.current;

        const handleColorChange = (e) => {
            const newScreeningCompoundInfoList = [...screeningCompoundInfoList];
            newScreeningCompoundInfoList[compoundIndex].baseColor = e.target.value;
            updateScreeningCompoundInfo(newScreeningCompoundInfoList);
        };
        el.addEventListener('change', handleColorChange);
        return () => {
            el.removeEventListener('change', handleColorChange);
        };
    }, []);

    const smiles = screeningCompoundInfoList[compoundIndex].smiles;
    return (
        <TableRow
            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
        >
            <TableCell align="left">
                <BMapsTooltip
                    title="Include this compound in the data"
                >
                    <Checkbox
                        checked={screeningCompoundInfoList[compoundIndex].active}
                        onChange={(event) => {
                            const newScreeningCompoundInfoList = [...screeningCompoundInfoList];
                            newScreeningCompoundInfoList[compoundIndex]
                                .active = event.target.checked;
                            updateScreeningCompoundInfo(newScreeningCompoundInfoList);
                        }}
                    />
                </BMapsTooltip>
            </TableCell>
            <TableCell align="left">
                {!editingName
                    ? (
                        <>
                            <BMapsTooltip noMaxWidth title="Change compound name for data display">
                                <IconButton onClick={() => setEditingName(true)}>
                                    <Edit />
                                </IconButton>
                            </BMapsTooltip>
                            {' '}
                            {getNumberedCompoundName(
                                screeningCompoundInfoList[compoundIndex], screeningCompoundInfoList
                            )}
                        </>
                    ) : (
                        <EditInPlaceInput
                            value={screeningCompoundInfoList[compoundIndex].name}
                            setValue={(value) => {
                                const newScreeningCompoundInfoList = [...screeningCompoundInfoList];
                                newScreeningCompoundInfoList[compoundIndex].name = value;
                                updateScreeningCompoundInfo(newScreeningCompoundInfoList);
                                setEditingName(false);
                            }}
                            stopEditing={() => setEditingName(false)}
                            Component={TextArea}
                        />
                    )}
            </TableCell>
            <TableCell align="left">
                <HoverableCompoundStructureCell
                    smiles={smiles}
                    uniqueId={smiles}
                    compoundName={screeningCompoundInfoList[compoundIndex].name}
                    imageStyles={{ height: '3.5rem', width: '10rem' }}
                    popoverImageStyles={{ height: '8rem' }}
                />
            </TableCell>
            <TableCell align="center">
                <BMapsTooltip
                    title={`Copy SMILES: ${smiles}`}
                    noMaxWidth
                >
                    <span>
                        {/* This span allows BMapsTooltip to work with CopyToClipboardButton */}
                        <CopyToClipboardButton
                            copyTarget={smiles}
                            tooltipMessage="Copied SMILES"
                            TooltipProps={{
                                placement: 'right',
                                arrow: true,
                                'aria-label': 'Copy SMILES',
                            }}
                            IconButtonProps={{ size: 'small' }}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <FileCopyIcon />
                                <span style={{ fontSize: 'small' }}>SMI</span>
                            </div>
                        </CopyToClipboardButton>
                    </span>
                </BMapsTooltip>
            </TableCell>
            {!!useColorOptions && (
                <TableCell align="right">
                    <input
                        ref={colorPickerRef}
                        type="color"
                        defaultValue={screeningCompoundInfoList[compoundIndex].baseColor}
                    />
                </TableCell>
            )}
        </TableRow>
    );
}
